// Map pixel coordinates on the video element to camera 0-8191 range
export const pixelToCameraCoord = (
  pixelVal: number,
  pixelMax: number
): number => Math.round((pixelVal / pixelMax) * 8191);

// Map camera 0-8191 range to pixel coordinates
export const cameraCoordToPixel = (
  cameraVal: number,
  pixelMax: number
): number => Math.round((cameraVal / 8191) * pixelMax);

/** Camera PositionABSFocus range (Minerva); stored in DDB and sent over MQTT/shadow as-is. */
export const MANUAL_FOCUS_MIN = 2000;

export const MANUAL_FOCUS_MAX = 4000;

export const defaultManualFocusDistance = (): number =>
  Math.round((MANUAL_FOCUS_MIN + MANUAL_FOCUS_MAX) / 2);

export const clampManualFocusDistance = (n: number): number =>
  Math.max(MANUAL_FOCUS_MIN, Math.min(MANUAL_FOCUS_MAX, n));

/**
 * Parse zone/UI string to camera focus units; invalid values use default midpoint.
 *
 * @param {string | null | undefined} s Stored `focusDistance` from DDB or UI state string.
 * @return {number} Integer camera PositionABSFocus value.
 */
export const parseStoredFocusDistance = (
  s: string | null | undefined
): number => {
  const n = parseInt(String(s ?? ""), 10);

  return Number.isFinite(n) ? n : defaultManualFocusDistance();
};

// Default center quarter rectangle in camera coordinates
export const DEFAULT_FOCUS_RECT = {
  x1: 2048,
  y1: 2048,
  x2: 6144,
  y2: 6144,
};

// Check if a rect is the default center quarter
export const isDefaultRect = (rect: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): boolean =>
  rect.x1 === 2048 && rect.y1 === 2048 && rect.x2 === 6144 && rect.y2 === 6144;

// Convert annotation polygon points to rect format
export const polygonToRect = (
  points: number[][]
): { x1: number; y1: number; x2: number; y2: number } => {
  const xs = points.map(p => p[0]);

  const ys = points.map(p => p[1]);

  return {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  };
};

// Convert rect to annotation polygon points (4 corners)
export const rectToPolygon = (rect: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): number[][] => [
  [rect.x1, rect.y1],
  [rect.x2, rect.y1],
  [rect.x2, rect.y2],
  [rect.x1, rect.y2],
];
